import { ActionTree } from "vuex";
import {
    GET_APPLICATION_VERSION,
    GET_USER,
    GET_ADMIN_USERS,
    GET_VIP_USERS,
    LOGIN,
    LOGOUT,
    SET_BLOB_SETTINGS,
} from "./mutations-types";
import { IAppState } from "./state";
import { authService, NotificationService, ResponseService } from "@/services";
import { IUser } from "@/models";

export const actions: ActionTree<IAppState, any> = {
    async fetchApplicationVersion(context): Promise<void> {
        const response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/GetApplicationVersion", {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });
        let appVersion: string;
        if (response.ok) {
            appVersion = await response.text();
        } else {
            appVersion = "0.0.0";
        }
        context.commit(GET_APPLICATION_VERSION, appVersion);
    },

    async getUser(context): Promise<void> {
        const response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/me", {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });
        const user: IUser = await response.json();
        context.commit(GET_USER, user);
    },

    async login(context): Promise<void> {
        if (!authService.getAccount()) {
            authService.login();
        }

        context.commit(LOGIN);
    },

    async logout(context): Promise<void> {
        authService.logout();
        context.commit(LOGOUT);
    },

    // Admin Users
    async fetchAdminUsers(context): Promise<void> {
        const response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/user?role=Admin", {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });
        const adminUsers = await response.json();
        context.commit(GET_ADMIN_USERS, adminUsers);
    },

    // Add Admin Users
    async addAdminUser(context, data): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/user/role", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Role");
        NotificationService.show(response, message);
        await context.dispatch("fetchAdminUsers");
        await context.dispatch("fetchVipUsers");
        return response.ok;
    },

    // VIP Users
    async fetchVipUsers(context): Promise<void> {
        const response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/user?role=Vip", {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });
        const vipUsers = await response.json();
        context.commit(GET_VIP_USERS, vipUsers);
    },

    // Add Vip Users
    async addVipUser(context, data): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/user/role", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Role");
        NotificationService.show(response, message);
        await context.dispatch("fetchAdminUsers");
        await context.dispatch("fetchVipUsers");
        return response.ok;
    },

    // Delete Users
    async deleteUser(context, userInfoId: number): Promise<boolean> {
        const response: Response =
            await fetch(`${process.env.VUE_APP_AZURE_FUNCTION_BASE_URL}/user/${userInfoId}/role`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${await authService.getAccessToken()}`,
                },
            });

        const message: string = await ResponseService.createMessage(response, "Role");
        NotificationService.show(response, message);
        await context.dispatch("fetchAdminUsers");
        await context.dispatch("fetchVipUsers");
        return response.ok;
    },

    // Add Admin Users
    async sendEmailToRequester(context, data): Promise<boolean> {
        const response: Response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/email", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
            body: JSON.stringify(data),
        });

        const message: string = await ResponseService.createMessage(response, "Email");
        NotificationService.show(response, message);
        return response.ok;
    },

    // Load storage configuration
    async loadStorageConfig(context): Promise<void> {
        const response = await fetch(process.env.VUE_APP_AZURE_FUNCTION_BASE_URL + "/blobSettings", {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${await authService.getAccessToken()}`,
            },
        });

        const blobSettings = await response.json();
        context.commit(SET_BLOB_SETTINGS, blobSettings);
    },

    clearStorageConfig(context): void {
        context.commit(SET_BLOB_SETTINGS, null);
    },
};
